import { useEffect, useState } from "react";
import { FileText, Download, Eye, CalendarDays, Tag, FolderOpen } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, resolveMedia } from "../lib/api";
import { useLang } from "../i18n";

export default function Reports() {
  const { pick } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/documents").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main>
      <Seo
        title="रिपोर्ट एवं दस्तावेज़ — पारदर्शिता"
        description="Anansha Welfare Society की वार्षिक रिपोर्ट, गतिविधि रिपोर्ट, पंजीकरण दस्तावेज़ और प्रमाणपत्र — पारदर्शिता के लिए सार्वजनिक।"
        path="/reports"
        breadcrumb={["रिपोर्ट"]}
      />
      <PageHero
        overline={{ hi: "पारदर्शिता", en: "Transparency" }}
        title={{ hi: "रिपोर्ट एवं दस्तावेज़", en: "Reports & Documents" }}
        description={{ hi: "संस्था की वार्षिक रिपोर्ट, गतिविधि रिपोर्ट, प्रमाणपत्र और अन्य महत्वपूर्ण दस्तावेज़।", en: "Annual reports, activity reports, certificates and other important documents of the organization." }}
        testid="reports-page-heading"
      />
      <section className="py-20 bg-ngo-cream min-h-[40vh]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-center text-ngo-ink/50 py-16">...</p>
          ) : items.length === 0 ? (
            <div data-testid="reports-empty" className="text-center py-20">
              <FolderOpen size={40} className="mx-auto text-ngo-deep/40" />
              <p className="mt-4 text-ngo-ink/60">{pick({ hi: "दस्तावेज़ जल्द ही यहां उपलब्ध होंगे।", en: "Documents will be available here soon." })}</p>
            </div>
          ) : (
            <div data-testid="reports-list" className="space-y-4">
              {items.map((d, i) => (
                <Reveal key={d.id} i={i % 4}>
                  <div data-testid={`report-card-${i}`} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 flex flex-wrap items-center gap-5 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(23,107,58,0.10)] transition-[box-shadow,transform] duration-300">
                    <div className="w-13 h-13 w-[52px] h-[52px] rounded-2xl bg-ngo-forest flex items-center justify-center text-ngo-gold shrink-0">
                      <FileText size={24} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <h2 className="font-heading font-bold text-lg text-ngo-ink">{d.title}</h2>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-ngo-ink/55">
                        {d.year && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {d.year}</span>}
                        {d.category && <span className="inline-flex items-center gap-1 text-ngo-leaf font-semibold"><Tag size={12} /> {d.category}</span>}
                      </div>
                      {d.description && <p className="mt-2 text-sm text-ngo-ink/65 leading-relaxed">{d.description}</p>}
                    </div>
                    <div className="flex gap-2.5">
                      <a data-testid={`report-view-${i}`} href={resolveMedia(d.file)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ngo-deep text-white text-sm font-semibold hover:bg-ngo-forest transition-colors duration-200">
                        <Eye size={14} /> {pick({ hi: "देखें", en: "View" })}
                      </a>
                      <a data-testid={`report-download-${i}`} href={resolveMedia(d.file)} download className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-ngo-deep text-ngo-deep text-sm font-semibold hover:bg-ngo-mint transition-colors duration-200">
                        <Download size={14} /> {pick({ hi: "डाउनलोड", en: "Download" })}
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
